import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface InterviewScheduleProps {
  domain: string;
}

interface Interview {
  id: string;
  applicationId: string;
  studentName: string;
  interviewDate: string;
  interviewTime: string;
  venue: string;
  interviewer: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  remarks?: string;
  scheduledOn: string;
}

const InterviewSchedule: React.FC<InterviewScheduleProps> = ({ domain }) => {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [domain]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load interviews
      const interviewsRef = collection(db, `school-data/${domain}/interviews`);
      const intSnapshot = await getDocs(interviewsRef);

      const intList: Interview[] = [];
      intSnapshot.forEach((docSnap) => {
        intList.push({
          id: docSnap.id,
          ...docSnap.data(),
        } as Interview);
      });
      setInterviews(intList);
    } catch (error: any) {
      console.error('Error loading data:', error);
      alert(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateInterviewStatus = async (interviewId: string, status: Interview['status']) => {
    try {
      const interviewRef = doc(db, `school-data/${domain}/interviews`, interviewId);
      await updateDoc(interviewRef, {
        status,
      });

      await loadData();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const deleteInterview = async (interviewId: string) => {
    if (!confirm('Are you sure you want to delete this interview?')) {
      return;
    }

    try {
      const interviewRef = doc(db, `school-data/${domain}/interviews`, interviewId);
      await deleteDoc(interviewRef);
      await loadData();
    } catch (error: any) {
      console.error('Error deleting interview:', error);
      alert(`Failed to delete interview: ${error.message}`);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-warning';
      case 'completed': return 'bg-success';
      case 'cancelled': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const todayInterviews = interviews.filter(int => {
    const today = new Date().toISOString().split('T')[0];
    return int.interviewDate === today && int.status === 'scheduled';
  });

  const upcomingInterviews = interviews.filter(int => {
    const today = new Date().toISOString().split('T')[0];
    return int.interviewDate > today && int.status === 'scheduled';
  });

  return (
    <div>
      <div className="row g-3 mb-4">
        {/* Statistics */}
        <div className="col-md-4">
          <div className="card bg-warning bg-opacity-10 border-0">
            <div className="card-body text-center">
              <h3 className="mb-0 text-warning">{todayInterviews.length}</h3>
              <small className="text-muted">Today's Interviews</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-primary bg-opacity-10 border-0">
            <div className="card-body text-center">
              <h3 className="mb-0 text-primary">{upcomingInterviews.length}</h3>
              <small className="text-muted">Upcoming Interviews</small>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-success bg-opacity-10 border-0">
            <div className="card-body text-center">
              <h3 className="mb-0 text-success">
                {interviews.filter(i => i.status === 'completed').length}
              </h3>
              <small className="text-muted">Completed</small>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Interviews */}
      {todayInterviews.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-warning bg-opacity-10">
            <h6 className="mb-0">
              <i className="bi bi-calendar-check me-2"></i>
              Today's Interviews ({new Date().toLocaleDateString()})
            </h6>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Time</th>
                    <th>Student Name</th>
                    <th>Application ID</th>
                    <th>Venue</th>
                    <th>Interviewer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {todayInterviews.map((interview) => (
                    <tr key={interview.id}>
                      <td><strong>{interview.interviewTime}</strong></td>
                      <td>{interview.studentName}</td>
                      <td>{interview.applicationId}</td>
                      <td>{interview.venue}</td>
                      <td>{interview.interviewer}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-success"
                            onClick={() => updateInterviewStatus(interview.id, 'completed')}
                            title="Mark Completed"
                          >
                            <i className="bi bi-check-circle"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => updateInterviewStatus(interview.id, 'cancelled')}
                            title="Cancel"
                          >
                            <i className="bi bi-x-circle"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* All Interviews */}
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0">
            <i className="bi bi-calendar-event me-2 text-primary-custom"></i>
            Interview Schedule
          </h5>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading interviews...</p>
            </div>
          ) : interviews.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-calendar-x display-1 text-muted"></i>
              <p className="mt-3 text-muted">No interviews scheduled</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Date & Time</th>
                    <th>Student Name</th>
                    <th>Application ID</th>
                    <th>Venue</th>
                    <th>Interviewer</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {interviews
                    .sort((a, b) => {
                      const dateA = new Date(`${a.interviewDate} ${a.interviewTime}`);
                      const dateB = new Date(`${b.interviewDate} ${b.interviewTime}`);
                      return dateB.getTime() - dateA.getTime();
                    })
                    .map((interview) => (
                      <tr key={interview.id}>
                        <td>
                          <div>
                            <strong>{new Date(interview.interviewDate).toLocaleDateString()}</strong>
                          </div>
                          <small className="text-muted">{interview.interviewTime}</small>
                        </td>
                        <td>{interview.studentName}</td>
                        <td>{interview.applicationId}</td>
                        <td>{interview.venue}</td>
                        <td>{interview.interviewer || '-'}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(interview.status)}`}>
                            {interview.status.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            {interview.status === 'scheduled' && (
                              <>
                                <button
                                  className="btn btn-outline-success"
                                  onClick={() => updateInterviewStatus(interview.id, 'completed')}
                                  title="Mark Completed"
                                >
                                  <i className="bi bi-check-circle"></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => updateInterviewStatus(interview.id, 'cancelled')}
                                  title="Cancel"
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              </>
                            )}
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => deleteInterview(interview.id)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewSchedule;
